import React, { useState } from "react";
import { Container, Button, TextField, Typography, Alert } from "@mui/material";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { styles } from "./styles/login.jsx";
import { handlePostLogin } from "../../utils/authUtils.js";
import { mfa } from "../../utils/securityService";
import MfaSetup from "../security/mfaSetup";

/**
 * Sign-in step for two-step sign-in (Nov 2026):
 *  - mode "code":  enter the code from the authenticator app (or the texted code)
 *  - mode "setup": staff who haven't set it up yet do so now (required for all staff)
 */
const MfaPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const style = styles(theme);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!state?.mode) return <Navigate to="/auth/login" />;

  const finish = async (data) => {
    if (data?.accessToken) await handlePostLogin({ data }, navigate);
    else setError("Please sign in again.");
  };

  const submitCode = async () => {
    setBusy(true); setError("");
    const res = await mfa({ action: "signin", session: state.session, username: state.username, email: state.email,
                            app: state.app, mfaMethod: state.mfaMethod, code });
    setBusy(false);
    if (res.ok && res.data?.accessToken) return finish(res.data);
    setError(res.message || "That code didn't work. Please try the newest code.");
    if (res.status === 401 && /expired/i.test(res.message || "")) setTimeout(() => navigate("/auth/login"), 2500);
  };

  return (
    <Container sx={style.loginFormWrapper}>
      <Typography sx={style.loginTitle}>Two-step sign-in</Typography>
      {state.mode === "code" ? (
        <>
          <Typography sx={style.loginSubTitle}>
            {state.mfaMethod === "sms"
              ? `Enter the 6-digit code we texted${state.destination ? ` to ${state.destination}` : ""}.`
              : "Open your authenticator app and enter the 6-digit code for DiscloSure."}
          </Typography>
          <TextField sx={style.loginInputTextbox} value={code} placeholder="123456" autoFocus
                     inputProps={{ inputMode: "numeric", maxLength: 7 }}
                     onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitCode()} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button sx={style.signInButton} variant="contained" disabled={busy || code.replace(/\D/g, "").length !== 6} onClick={submitCode}>
            {busy ? "Checking…" : "Sign In"}
          </Button>
          <Typography sx={{ fontSize: 13, mt: 2 }}>
            Lost your phone or changed apps? Ask your school or district administrator to reset your two-step sign-in.
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={style.loginSubTitle}>
            To protect student information, every staff account now needs two-step sign-in. It takes about a minute.
          </Typography>
          <MfaSetup setupId={state.setupId} smsAvailable={state.smsAvailable} onDone={finish} />
        </>
      )}
      <Typography sx={style.loginForgotPassword} onClick={() => navigate("/auth/login")}>Back to sign in</Typography>
    </Container>
  );
};

export default MfaPage;
